import { identityBaseUrl as baseUrl, authHeaders, handle } from './identityHttp';

// Super-admin User Management — talks to Identity-Api directly (all routes are
// admin/bearer-guarded and scope-checked server-side). Backing the "User Management"
// tab of the school/group account view. Payload shapes mirror the Identity swagger.

const url = (path) => `${baseUrl()}${path}`;

// --- Reads -----------------------------------------------------------------

// POST /users/search — the primary list. Scope a school view with { schoolIds: [id] }
// and a group view with { groupIds: [id] }. Supports sortBy/sortDescending + paging.
export async function searchUsers(request) {
  const body = await handle(
    await fetch(url('/api/v1/users/search'), {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ includeRoles: true, includeScopes: true, ...request }),
    })
  );
  return {
    users: body?.users || body?.items || body?.data || (Array.isArray(body) ? body : []),
    total: body?.total ?? body?.totalCount ?? body?.count ?? undefined,
    raw: body,
  };
}

// POST /roles/search — the canonical role catalogue (Name/Code/Description/IsActive).
// The Add User role picker sources its labels + descriptions from here rather than
// hardcoding them, so the copy stays owned by Identity. Active roles only by default.
export async function searchRoles({ isActive = true, page = 1, pageSize = 100 } = {}) {
  const body = await handle(
    await fetch(url('/api/v1/roles/search'), {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ isActive, page, pageSize }),
    })
  );
  return body?.roles || body?.items || body?.data || (Array.isArray(body) ? body : []);
}

// GET /user-management/{userId}/activity — the "Reveal History" timeline (merged
// logins + MFA outcomes + admin-initiated password/MFA actions, newest first).
export async function getUserActivity(userId, { page = 1, pageSize = 30 } = {}) {
  const qs = new URLSearchParams({ page, pageSize }).toString();
  return handle(
    await fetch(url(`/api/v1/user-management/${encodeURIComponent(userId)}/activity?${qs}`), {
      headers: await authHeaders(),
    })
  );
}

// GET /audit — the global audit timeline (admin actions + auth events, newest first).
// Every filter is optional; only the ones that carry a value are put on the query string so
// the backend sees a clean request. The camelCase page state maps onto the PascalCase params
// the endpoint binds (ActorUserId/Action/TargetType/TargetId/From/To/Page/PageSize).
export async function getAuditLog(filters = {}) {
  const map = {
    ActorUserId: filters.actorUserId,
    Search: filters.search,
    Action: filters.action,
    TargetType: filters.targetType,
    TargetId: filters.targetId,
    From: filters.from,
    To: filters.to,
    Page: filters.page,
    PageSize: filters.pageSize,
  };
  const params = new URLSearchParams();
  Object.entries(map).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, value);
  });
  const qs = params.toString();
  return handle(
    await fetch(url(`/api/v1/audit${qs ? `?${qs}` : ''}`), {
      headers: await authHeaders(),
    })
  );
}

// GET /audit/users/{userId} — the same timeline scoped to activity performed BY one super
// admin, powering the "Reveal History" drill-down in Super Management.
export async function getSuperAdminHistory(userId, { page = 1, pageSize = 30 } = {}) {
  const qs = new URLSearchParams({ Page: page, PageSize: pageSize }).toString();
  return handle(
    await fetch(url(`/api/v1/audit/users/${encodeURIComponent(userId)}?${qs}`), {
      headers: await authHeaders(),
    })
  );
}

// --- Writes (account actions) ----------------------------------------------

// POST /user-invitations/invite-with-access — atomic add-user (roles + scopes,
// seat-limit checked before any write). `scopeAssignments` items:
// { scopeType, entityIds:[], isWildcardAccess?, parentGroupId? }.
export async function inviteUserWithAccess(payload) {
  return handle(
    await fetch(url('/api/v1/user-invitations/invite-with-access'), {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    })
  );
}

// DELETE /users/{userId} — archives locally, removes from the identity provider.
export async function deleteUser(userId, reason = 'Deleted by super admin') {
  // The endpoint binds a required DeleteUserRequest { reason } from the body (used as the
  // audit reason); a bodyless DELETE is rejected as a 400, so always send one.
  return handle(
    await fetch(url(`/api/v1/users/${encodeURIComponent(userId)}`), {
      method: 'DELETE',
      headers: await authHeaders(),
      body: JSON.stringify({ reason }),
    })
  );
}

// PUT /users/{userId}/email — changes email locally + in the provider.
// A duplicate address is rejected 409 (error.status === 409 on the thrown error).
export async function changeUserEmail(userId, newEmail) {
  return handle(
    await fetch(url(`/api/v1/users/${encodeURIComponent(userId)}/email`), {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ newEmail }),
    })
  );
}

// POST /auth/send-set-password-link — "Reset password": sends an invite (no password)
// or a reset link (has password). Pass whichever identifier you hold.
export async function sendSetPasswordLink({ userId, email }) {
  return handle(
    await fetch(url('/api/v1/auth/send-set-password-link'), {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ userId, email }),
    })
  );
}

// POST /users/{userId}/reset-mfa — clears the broken enrollment but KEEPS MFA
// required, forcing a fresh enrolment on next login (not the same as disabling MFA).
export async function resetUserMfa(userId) {
  return handle(
    await fetch(url(`/api/v1/users/${encodeURIComponent(userId)}/reset-mfa`), {
      method: 'POST',
      headers: await authHeaders(),
    })
  );
}

// POST /users/{userId}/reactivate — revives a suspended/inactive/deleted user.
export async function reactivateUser(userId) {
  return handle(
    await fetch(url(`/api/v1/users/${encodeURIComponent(userId)}/reactivate`), {
      method: 'POST',
      headers: await authHeaders(),
    })
  );
}

// GET /MfaPolicy/by-entity — the current org-level MFA policies for a School/Group.
// entityType is the integer ScopeType. The response is an untyped list of policy rows;
// interpretMfaEnrolled below decides whether org-level MFA is on.
export async function getOrgMfaEnrollment({ entityType, entityId }) {
  const qs = new URLSearchParams({ entityType, entityId }).toString();
  return handle(
    await fetch(url(`/api/v1/MfaPolicy/by-entity?${qs}`), {
      headers: await authHeaders(),
    })
  );
}

// PUT /MfaPolicy/org-enrollment — organisation-level MFA toggle for a School/Group.
// entityType is the integer ScopeType; enabling ensures a Required policy. For a School with
// a `parentGroupId` whose group enforces MFA, disabling writes an explicit school-level
// opt-out (Optional) that overrides the group; otherwise disabling removes the policy.
export async function setOrgMfaEnrollment({ entityType, entityId, enabled, parentGroupId }) {
  const body = { entityType, entityId, enabled };
  if (parentGroupId !== undefined && parentGroupId !== null) body.parentGroupId = parentGroupId;
  return handle(
    await fetch(url('/api/v1/MfaPolicy/org-enrollment'), {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(body),
    })
  );
}
